import type { SubmitPartnerConsentBody } from './types';

interface UADataValues {
  platform?: string;
  platformVersion?: string;
  architecture?: string;
  model?: string;
  uaFullVersion?: string;
  fullVersionList?: Array<{ brand: string; version: string }>;
  bitness?: string;
  mobile?: boolean;
}

interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

const HIGH_ENTROPY_HINTS = [
  'platform',
  'platformVersion',
  'architecture',
  'model',
  'uaFullVersion',
  'fullVersionList',
  'bitness',
];

async function readUserAgentData(): Promise<string | null> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData })
    .userAgentData;
  if (!uaData?.getHighEntropyValues) return null;
  try {
    const values = await uaData.getHighEntropyValues(HIGH_ENTROPY_HINTS);
    return JSON.stringify(values);
  } catch {
    return null;
  }
}

async function computeFingerprint(
  parts: Array<string | number | null>,
): Promise<string | null> {
  try {
    const input = parts.map((part) => String(part ?? '')).join('|');
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(input),
    );
    const hex = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
    return `fp_${hex.slice(0, 16)}`;
  } catch {
    return null;
  }
}

export async function collectClientContext(args: {
  sessionId: string;
  displayLanguage: string | null;
}): Promise<SubmitPartnerConsentBody> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  const screenWidth = window.screen?.width ?? null;
  const screenHeight = window.screen?.height ?? null;
  const devicePixelRatio = window.devicePixelRatio ?? null;
  const colorDepth = window.screen?.colorDepth ?? null;
  const browserLanguage = navigator.language || null;

  const [deviceFingerprint, userAgentData] = await Promise.all([
    computeFingerprint([
      navigator.userAgent,
      browserLanguage,
      timezone,
      `${screenWidth}x${screenHeight}`,
      devicePixelRatio,
      colorDepth,
    ]),
    readUserAgentData(),
  ]);

  return {
    sessionId: args.sessionId,
    referrer: document.referrer || null,
    browserLanguage,
    displayLanguage: args.displayLanguage,
    clientSubmittedAt: new Date().toISOString(),
    timezone,
    timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
    screenWidth,
    screenHeight,
    devicePixelRatio,
    colorDepth,
    deviceFingerprint,
    userAgentData,
  };
}
